import React, {useCallback, useMemo, useState} from "react";
// import './AccountSetting.scss'
import styles from './AccountSetting.module.scss'
import { SETTING_CONTENTS_NAMES} from "config/settingsConfig";
import Input from "@components/Input";
import Button from "@components/Button";
import { SETTING_ACCESS_CONTORL_COLUMN } from "config/tableColumns";
import Table from "@components/Table";
import useGetUser, { useDeleteUser } from "@hooks/User/useUser";
import { BaseUserSchema, initialBaseUser, UserRoleId, UserStatusId } from "@models/User";
import { ReactComponent as Close } from 'img/btn-close.svg';
import Modal from "@components/Modal";
import { SearchItemsProps } from "utils/searchUtil";
import { useAuthPage } from "@features/auth/useAuthPage";
import useModalActions from "@hooks/common/useModalAction";
import { ModalResponse } from "config/labels";
import useAuth from "@hooks/Auth/useAuth";
import { useModal } from "@components/Modal/ModalProvider";
import Switch from "@components/Switch";
import { patchUser, postUser } from "@api/user";
import { patchPassword } from "@api/auth";
import AccountTable from "@components/Table/AccountTable";
import { useNavigate } from "react-router-dom";

interface ExtendedUserSchema extends BaseUserSchema {
  id?: string;
}
export interface Props {
  onClose: () => void;
  data?: ExtendedUserSchema;
  mode: 'add' | 'modify';
  refetch: () => void;
}

export function searchItems({ query, items }: SearchItemsProps): any[] {
  const q = query.toLowerCase().trim();
  if (!q) return items;

  const keywords = q.split(/\s+/);

  const filtered = items.filter((item) => {
    const searchable = Object.values(item)
      .filter((v) => typeof v === "string")
      .join(" ")
      .toLowerCase();

    return keywords.every((word) => searchable.includes(word));
  });

  return filtered.sort((a, b) => {
    const aStr = Object.values(a).find((v) => typeof v === "string") || "";
    const bStr = Object.values(b).find((v) => typeof v === "string") || "";
    return String(aStr).localeCompare(String(bStr));
  });
}
function UserModal({
  onClose,
  data = initialBaseUser,
  mode,
  refetch
}: Props){
  const { errorDefaultAction } = useModalActions();
  const { accessToken } = useAuth();
  const [name, setName] = useState<string>(data.name ?? '');
  const [password, setPassword] = useState<string>('');
  const [showPass, setShowPass] = useState<boolean>(false);
  const [email, setEmail] = useState<string>(data.email ?? '');
  const [userStatus, setUserStatus] = useState<UserStatusId>(
    (data.userStatus?.id as UserStatusId) ?? UserStatusId.Active
  );
  const isChecked = userStatus === UserStatusId.Active;
  const [note, setNote] = useState<string>(data.note ?? '');

  const handleSwitchChange = (checked: boolean) => {
    setUserStatus(checked ? UserStatusId.Active : UserStatusId.Deactive);
  };

  const handleSubmit = async () => {
    if (!email){
      errorDefaultAction(`${SETTING_CONTENTS_NAMES.account.emailR}`);
      return;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      errorDefaultAction(`${SETTING_CONTENTS_NAMES.account.emailInvalid}`);
      return;
    }
    if (!name){
      errorDefaultAction(`${SETTING_CONTENTS_NAMES.account.nameR}`);
      return;
    }
    if (mode !== 'modify') {
      if (!password) {
        errorDefaultAction(SETTING_CONTENTS_NAMES.account.passwordR);
        return;
      }
      const passwordValidationError = getPasswordValidationError(password);
      if (passwordValidationError) {
        errorDefaultAction(passwordValidationError);
        return;
      }
    }

    const params: BaseUserSchema = {
      email: email,
      password: password,
      name: name,
      isGuest: true,
      userStatus: { id : userStatus},
      role: {id : '000000000000000000000004'},
      note: note
    }

    try {
      if (mode === 'modify' && data?.id) {
        const updatedUser: Partial<BaseUserSchema> = {
          name: name,
          email: email,
          userStatus: { id : userStatus},
          note: note,
        };

        await patchUser({ accessToken, params : updatedUser, id: data.id});
        refetch();
      } else {
        await postUser({ accessToken, params });
        refetch();
      }

      onClose();
    } catch (error) {
      console.error('❌ user submit failed:', error);
      errorDefaultAction('An unexpected error occurred.');
    }
  }


  return (
    <Modal
      type="page"
      close={onClose}
      description={
        <div className={styles.modalContainer}>
          {/* 상단 */}
          <div className={styles.modalHeader}>
            <span className={styles.modalHeaderLabel}>{SETTING_CONTENTS_NAMES.account.userPopup}</span>
            <Close className="pacs-server-title-close" onClick={onClose} />
          </div>

          {/* 입력폼 */}
          <div className={styles.modalBlock}>
            <div className={styles.modalLabelInput}>
              <span className={styles.modalLILabel}>{SETTING_CONTENTS_NAMES.account.name}</span>
              <Input
                className="setting-account-modal"
                placeholder="Name"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </div>
            <div className={styles.modalLabelInput}>
              <span className={styles.modalLILabel}>{SETTING_CONTENTS_NAMES.account.email}</span>
              <Input
                className="setting-account-modal"
                placeholder="Email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
            {mode === 'modify' ? null : (
              <div className={styles.modalLabelInput}>
                <span className={styles.modalLILabel}>{SETTING_CONTENTS_NAMES.account.password}</span>
                <Input
                  className="setting-account-modal"
                  type="password"
                  placeholder="Password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  showPassword={showPass}
                  onTogglePassword={() => setShowPass((prev) => !prev)}
                />
              </div>
            )}
            <div className={styles.modalLabelInput}>
              <span className={styles.modalLILabel}>{SETTING_CONTENTS_NAMES.account.userStatus}</span>
              <div className={styles.modalLabelSwitch}>
                <Switch checked={isChecked} onChange={handleSwitchChange}/>
                <span className={styles.modalLabelSwitchText}>{userStatus === UserStatusId.Active ? "Active" : "Deactive"}</span>
              </div>
            </div>
            <div className={`${styles.modalLabelInput} ${styles.modalNoteField}`}>
              <span className={styles.modalLILabel}>{SETTING_CONTENTS_NAMES.account.note}</span>
              <Input
                className="setting-account-modal-note"
                placeholder="Note"
                value={note}
                onChange={(e) => setNote(e.target.value)}
              />
            </div>
          </div>

          {/* 버튼 */}
          <div className={styles.modalAction}>
            {/* <div className="pacs-server-buttons-container"> */}
              <Button
              className='action-control cancel'
              onClick={onClose}>{SETTING_CONTENTS_NAMES.pacsConnection.cancel}</Button>
              <Button
              className='action-control confirm'
              color="cardio"
                onClick={handleSubmit}
              >
                {SETTING_CONTENTS_NAMES.pacsConnection.confirm}
              </Button>
            {/* </div> */}
          </div>
        </div>
      }
    />
  )
}


const formatPasswordMissingRequirements = (requirements: string[]): string => {
  if (requirements.length === 1) {
    return requirements[0];
  }

  return `${requirements.slice(0, -1).join(', ')} and ${requirements[requirements.length - 1]}`;
};

const getPasswordValidationError = (pw: string): string | null => {
  const missingRequirements: string[] = [];

  if (pw.length < 8) {
    missingRequirements.push('at least 8 characters');
  }

  if (!/[A-Z]/.test(pw)) {
    missingRequirements.push('an uppercase letter');
  }

  if (!/[a-z]/.test(pw)) {
    missingRequirements.push('a lowercase letter');
  }

  if (!/[0-9]/.test(pw)) {
    missingRequirements.push('a number');
  }

  if (!/[^A-Za-z0-9]/.test(pw)) {
    missingRequirements.push('a special character');
  }

  if (missingRequirements.length === 0) {
    return null;
  }

  return `Password must include ${formatPasswordMissingRequirements(missingRequirements)}.`;
};


interface AccountSettingProps {}
function AccountSetting({
  children,
}: AccountSettingProps & {
  children?: React.ReactNode;
}): React.ReactElement {
  const { data, refetch } = useGetUser();
  const { accessToken } = useAuth();
  const deleteMutation = useDeleteUser();
  const [selectedKey, setSelectedKey] = useState<string | undefined>(undefined);
  const [selectedRow, setSelectedRow] = useState<ExtendedUserSchema | undefined>();
  const [Search, setSearch] = useState<string>("");
  const { errorDefaultAction } = useModalActions();
  const { logout } = useAuth();
  const { user } = useAuthPage();
  const { openModal, closeModal,closeTopNModals } = useModal();
  const navigator=useNavigate()
  const [formData, setFormData] = useState({
    accountName: "",
    currentPw: "",
    newPw: "",
    confirmPw: ""
  });

  const [showPasswordState, setShowPasswordState] = useState({
    currentPw: false,
    newPw: false,
    confirmPw: false
  });

  const [errorState, setErrorState] = useState({
    currentPw: false,
    newPw: false,
    confirmPw: false
  });
  const isAdmin = Boolean(
    user?.role?.isAdmin || user?.role?.name?.toLowerCase() === 'admin'
  );

  const inputFields = [
    {
      key: "accountName",
      label: SETTING_CONTENTS_NAMES.account.accountName,
      placeholder: SETTING_CONTENTS_NAMES.account.name,
      type: "text"
    },
    {
      key: "currentPw",
      label: SETTING_CONTENTS_NAMES.account.currentPassword,
      placeholder: SETTING_CONTENTS_NAMES.account.currentPassword,
      type: "password"
    },
    {
      key: "newPw",
      label: SETTING_CONTENTS_NAMES.account.newPassword,
      placeholder: SETTING_CONTENTS_NAMES.account.newPassword,
      type: "password"
    },
    {
      key: "confirmPw",
      label: SETTING_CONTENTS_NAMES.account.confirmPassword,
      placeholder: SETTING_CONTENTS_NAMES.account.confirmPassword,
      type: "password"
    }
  ] as const;

  const filteredData = useMemo(() => {
    if (!data) return [];
    return searchItems({ query: Search, items: data });
  }, [Search, data]);

  const handleSave = async() => {
    if (!formData.accountName || !formData.currentPw || !formData.newPw || !formData.confirmPw) {
      errorDefaultAction(ModalResponse.FILL_ALL_FIELDS)
      return;
    }

    if (!isAdmin && formData.accountName != user?.name){
      errorDefaultAction(ModalResponse.NAME_SELF_ONLY)
      return;
    }

    if (formData.currentPw === formData.confirmPw) {
      errorDefaultAction(ModalResponse.PASSWORD_SAME)
      return;
    }

    if (formData.newPw != formData.confirmPw){
      errorDefaultAction(ModalResponse.PASSWORD_MISMATCH)
      return;
    }

    if(!isAdmin && formData.accountName != user?.name){
      errorDefaultAction(ModalResponse.PASSWORD_SELF_ONLY)
      return;
    }

    const newPasswordValidationError = getPasswordValidationError(formData.newPw);
    if (newPasswordValidationError) {
      errorDefaultAction(newPasswordValidationError);
      return;
    }

    try {
      await patchPassword({
        accessToken,
        isAdmin,
        body: {
          user_name: formData.accountName,
          password: formData.newPw,
          oldPassword: formData.currentPw,
        },
      });

      if (!isAdmin || formData.accountName === user?.name) {
        logout();
      } else {
        setFormData((prev) => ({
          ...prev,
          currentPw: '',
          newPw: '',
          confirmPw: '',
        }));
      }
    } catch (error: any) {
      const serverMessage = error?.response?.data?.message;

      if (serverMessage === "incorrectOldPassword") {
        errorDefaultAction(ModalResponse.INCORRECT_OLD_PASSWORD);
        return;
      }

      errorDefaultAction(ModalResponse.PASSWORD_CHANGED_FAIL);
    }
  }

  const handleOpenUserModal = useCallback((mode: 'add' | 'modify') => {
    let modalId = '';
    modalId = openModal(
      <UserModal
        mode={mode}
        data={mode === 'modify' ? selectedRow : undefined}
        onClose={() => closeModal(modalId)}
        refetch={refetch}
      />
    )
  }, [openModal, closeModal, selectedRow]);

  return (
    <div className={styles.container}>
      <div className={styles.frame}>
        <span className={styles.frameHeader}>
          {SETTING_CONTENTS_NAMES.account.preference}
        </span>
        <div className={styles.frameDivider} />
        <div className={styles.block}>
          <div className={styles.content}>
            <div className={styles.contentLayer}>
              <div className={styles.layer1}>
                {inputFields.map(({ key, label, placeholder, type }, index) => (
                  <div key={key} className={styles.labelInput}>
                    <span className={styles.labelLabel}> {label} </span>
                    <Input
                      type={type}
                      placeholder={placeholder}
                      value={formData[key]}
                      className="setting-account-input"
                      error={
                        type === 'password'
                          ? errorState[key as keyof typeof errorState]
                          : false
                      }
                      showPassword={
                        type === 'password'
                          ? showPasswordState[
                              key as keyof typeof showPasswordState
                            ]
                          : undefined
                      }
                      onTogglePassword={
                        type === 'password'
                          ? () =>
                              setShowPasswordState((prev) => ({
                                ...prev,
                                [key]:
                                  !prev[key as keyof typeof showPasswordState],
                              }))
                          : undefined
                      }
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          [key]: e.target.value,
                        }))
                      }
                    />
                  </div>
                ))}
              </div>
              <div className={styles.layer2}>
                {/* <div className="account-container-preference-content-grid2-1"> */}
                <div className={styles.label}>
                  <div className={styles.labelInput}>
                    <span className={styles.labelLabel}>
                      {' '}
                      {SETTING_CONTENTS_NAMES.account.lastLogin}{' '}
                    </span>
                    <div className={styles.label}>
                      <div className={styles.numberInput}>
                        <span className={styles.labelLabel}>
                          {user?.lastLogin &&
                            (() => {
                              const d = new Date(user.lastLogin);
                              const yyyy = d.getFullYear();
                              const MM = String(d.getMonth() + 1).padStart(
                                2,
                                '0',
                              );
                              const dd = String(d.getDate()).padStart(2, '0');
                              const hh = String(d.getHours()).padStart(2, '0');
                              const mm = String(d.getMinutes()).padStart(
                                2,
                                '0',
                              );
                              return `${yyyy}-${MM}-${dd} ${hh}:${mm}`;
                            })()}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* <div className={styles.label}> */}
                {/* </div> */}
                {/* </div> */}
                {/* <div className="account-container-preference-content-grid2-2">
              <div>
                {SETTING_CONTENTS_NAMES.account.group}
              </div>
              <div>
                Group A
              </div>
            </div> */}
              </div>
              <div className={styles.layer3}>
                <Button className="action-control save" onClick={handleSave}>
                  {SETTING_CONTENTS_NAMES.account.save}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
      {isAdmin && (
        <div className={styles.frame}>
          <span className={styles.frameHeader}>
            {SETTING_CONTENTS_NAMES.account.accessControl}
          </span>
          <div className={styles.frameDivider} />
          <div className={styles.block2}>
            <div className={styles.content2}>
              <div className={styles.label2}>
                <span className={styles.label2Label}>
                  {' '}
                  {SETTING_CONTENTS_NAMES.account.userList}
                </span>
              </div>
              <div className={styles.ListContainer}>
                <div className={styles.listHeader}>
                  <div className={styles.numberInput}>
                    <Button
                      className={`action-control add`}
                      onClick={() => handleOpenUserModal('add')}
                    >
                      {SETTING_CONTENTS_NAMES.account.add}
                    </Button>

                    <Button
                      className={`action-control modify`}
                      disabled={!selectedKey}
                      onClick={() => handleOpenUserModal('modify')}
                    >
                      {SETTING_CONTENTS_NAMES.account.modify}
                    </Button>
                    <Button
                      className={`action-control modify`}
                      onClick={() => {
                        closeTopNModals(1);
                        navigator('/backoffice');
                      }}
                    >
                      {SETTING_CONTENTS_NAMES.account.backoffice}
                    </Button>
                  </div>

                  <div className={styles.input}>
                    <Input
                      className="setting-account-control"
                      type="search"
                      value={Search}
                      placeholder="Search"
                      onChange={(e: any) => setSearch(e.target.value)}
                    />
                  </div>
                </div>
              </div>
              <div className={styles.contentTable}>
                {data ? (
                  <AccountTable
                    className="table-container"
                    columns={SETTING_ACCESS_CONTORL_COLUMN}
                    data={filteredData.map((item) => ({
                      ...item,
                      roleName: item.role?.name ?? '',
                      statusName: item.userStatus?.name ?? '',
                    }))}
                    getRowId={(r) => r.id}
                    onRowClick={(key, row) => {
                      const { roleName, statusName, ...rest } = row;
                      setSelectedKey(key.toString());
                      setSelectedRow(rest as ExtendedUserSchema);
                    }}
                    selectedKey={selectedKey}
                  />
                ) : null}
              </div>

              <div className={`${styles.btnDelect}`}>
                <Button
                  className={`action-control delete`}
                  disabled={!selectedKey}
                  onClick={() => deleteMutation.mutate(selectedKey!)}
                >
                  {SETTING_CONTENTS_NAMES.account.delete}
                </Button>
              </div>

              <div className="account-container-access-content-task">
                <div className="account-setting-flex-box">
                  {/* <Button
                disabled = {!selectedKey}
              >
                {SETTING_CONTENTS_NAMES.account.disconnection}
              </Button> */}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AccountSetting;
